import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import Button from "../../../../components/ui/Button";
import AddButton from "../../../../components/ui/AddButton";
import ToggleSwitch from "../../../../components/common/ToggleSwitch";
import DeleteModal from "../../../../components/modals/DeleteModal";
import {
    getProgressionConfig,
    getRewards,
    deleteReward,
    updateRewardStatus,
    RewardResponse,
    getLevels,
} from "../../../../api/progression";
import i18n from "../../../../i18n";
import styles from "./ManageRewards.module.css";
import Icon from "../../../../components/common/Icon";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface ManageRewardsProps {
    navigateToNext: () => void;
}

const ManageRewards: React.FC<ManageRewardsProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";

    const [rewards, setRewards] = useState<RewardResponse[]>([]);
    const [levels, setLevels] = useState<any[]>([]);
    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [rewardToDelete, setRewardToDelete] = useState<RewardResponse | null>(null);

    const linkId = trackData?.trackId || trackData?.id;

    const loadRewards = async () => {
        if (!trackData.trackId) return;

        try {
            setLoading(true);
            const configResponse = await getProgressionConfig({ token, trackId: trackData.trackId });
            if (configResponse?.progressionConfig) {
                setProgressionConfigId(configResponse.progressionConfig.id);
                const [rewardsResponse, levelsResponse] = await Promise.all([
                    getRewards({
                        token,
                        progressionConfigId: configResponse.progressionConfig.id,
                    }),
                    getLevels({
                        token,
                        progressionConfigId: configResponse.progressionConfig.id,
                    }),
                ]);
                if (rewardsResponse?.rewards) {
                    setRewards(rewardsResponse.rewards);
                }
                if (levelsResponse?.levels) {
                    setLevels(levelsResponse.levels);
                }
            }
        } catch (error) {
            console.error("Error loading rewards:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRewards();
        // fetch-on-mount when track changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, token]);

    const handleToggleStatus = async (rewardId: string, currentStatus: boolean) => {
        // Optimistic update
        setRewards((prev) =>
            prev.map((reward) => (reward.id === rewardId ? { ...reward, status: !currentStatus } : reward)),
        );
        try {
            await updateRewardStatus({
                token,
                rewardId,
                status: !currentStatus,
            });
        } catch (error) {
            console.error("Error updating reward status:", error);
            // Revert on error
            setRewards((prev) =>
                prev.map((reward) => (reward.id === rewardId ? { ...reward, status: currentStatus } : reward)),
            );
        }
    };

    const handleDelete = async () => {
        if (!rewardToDelete) return;

        // Optimistic update
        const deletedReward = rewardToDelete;
        setRewards((prev) => prev.filter((reward) => reward.id !== deletedReward.id));
        setDeleteModalOpen(false);
        setRewardToDelete(null);

        try {
            await deleteReward({ token, rewardId: deletedReward.id });
        } catch (error) {
            console.error("Error deleting reward:", error);
            // Revert on error
            setRewards((prev) => [...prev, deletedReward]);
        }
    };

    const handleEdit = (rewardId: string) => {
        navigate(`/create/tour/${linkId}/rewards/modify/${rewardId}`);
    };

    const handleCreate = () => {
        navigate(`/create/tour/${linkId}/rewards/create`);
    };

    const getLocalizedName = (reward: RewardResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = reward.rewardLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || reward.rewardLocalized?.[0]?.name || t("unnamed");
    };

    const getLocalizedDescription = (reward: RewardResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = reward.rewardLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.descriptionText || reward.rewardLocalized?.[0]?.descriptionText || "";
    };

    const getLevelInfo = (reward: RewardResponse) => {
        if (!reward.levelId) return null;
        const level = levels.find((l) => l.id === reward.levelId);
        if (!level) return null;
        return {
            position: level.position,
            name: (() => {
                const langCode = i18n.language?.toUpperCase();
                const localized = level.levelLocalized?.find(
                    (loc: { id: string; language: string; name: string; text?: string }) =>
                        loc.language?.toUpperCase() === langCode,
                );
                return localized?.name || level.levelLocalized?.[0]?.name || `${t("level")} ${level.position}`;
            })(),
        };
    };

    const getConditionDescription = (reward: RewardResponse) => {
        const levelInfo = getLevelInfo(reward);
        if (levelInfo) {
            return t("unlocksAtLevel", { level: levelInfo.position });
        }
        return getLocalizedDescription(reward);
    };

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/badges`}>{t("progression")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("rewards")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createAndManageRewards")}</h2>
                        <p className={pageHeader.subtitle}>{t("rewardsRewardVisitorsProgress")}</p>
                    </div>
                    <div className={styles.headerActions}>
                        <AddButton onClick={handleCreate} tooltip={t("addNewReward")} disabled={!progressionConfigId} />
                    </div>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>{t("loading")}</div>
            ) : rewards.length === 0 ? (
                <div className={styles.emptyState}>
                    <h3 className={styles.emptyStateTitle}>{t("noRewardsYet")}</h3>
                    <p className={styles.emptyStateText}>{t("noRewardsDescription")}</p>
                    <AddButton
                        onClick={handleCreate}
                        tooltip={t("createFirstReward")}
                        disabled={!progressionConfigId}
                    />
                </div>
            ) : (
                <div className={styles.rewardsList}>
                    {rewards.map((reward) => {
                        const levelInfo = getLevelInfo(reward);
                        const conditionDesc = getConditionDescription(reward);
                        return (
                            <div
                                key={reward.id}
                                className={`${styles.rewardCard} ${!reward.status ? styles.rewardCardInactive : ""}`}
                                onClick={() => handleEdit(reward.id)}
                            >
                                {/* Reward image */}
                                {reward.imageUrl ? (
                                    <div className={styles.rewardImageContainer}>
                                        <img
                                            src={reward.imageUrl}
                                            alt={getLocalizedName(reward)}
                                            className={styles.rewardImage}
                                        />
                                    </div>
                                ) : (
                                    <div className={styles.rewardImageContainer}>
                                        <span className={styles.rewardImagePlaceholder}>🎁</span>
                                    </div>
                                )}

                                {/* Reward info */}
                                <div className={styles.rewardInfo}>
                                    <h4 className={styles.rewardName}>{getLocalizedName(reward)}</h4>
                                    <p className={styles.rewardDescription}>
                                        {conditionDesc || getLocalizedDescription(reward) || t("noDescription")}
                                    </p>
                                    {levelInfo && (
                                        <div className={styles.rewardMeta}>
                                            <span className={styles.metaTag}>
                                                {t("level")} {levelInfo.position}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Reward actions */}
                                <div className={styles.rewardActions}>
                                    <ToggleSwitch
                                        checked={reward.status}
                                        onChange={() => handleToggleStatus(reward.id, reward.status)}
                                    />
                                    <div className={styles.actionButtons}>
                                        <Tooltip title={t("edit")} arrow>
                                            <button
                                                type="button"
                                                className={styles.editBtn}
                                                aria-label={t("edit")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleEdit(reward.id);
                                                }}
                                            >
                                                <Icon name="ic-edit" size={20} />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title={t("delete")} arrow>
                                            <button
                                                type="button"
                                                className={styles.deleteBtn}
                                                aria-label={t("delete")}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setRewardToDelete(reward);
                                                    setDeleteModalOpen(true);
                                                }}
                                            >
                                                <Icon name="ic-trash" size={20} />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Continue — mock .foot (rewards optional) */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={rewards.length === 0 ? t("skipAndContinue") : t("continue")}
                    onClick={navigateToNext}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>

            {/* Delete confirmation modal */}
            {rewardToDelete && (
                <DeleteModal
                    isOpen={deleteModalOpen}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setRewardToDelete(null);
                    }}
                    onConfirm={handleDelete}
                    message={t("deleteRewardConfirmation")}
                    title={t("deleteReward")}
                />
            )}
        </div>
    );
};

export default ManageRewards;
